import { getMainWindowComposeViewState } from 'owa-mail-compose-actions/lib/selectors/getMainWindowComposeViewState';
import { isEncryptionAvailable } from './isEncryptionAvailable';
import type { RibbonControlDefinitionProps } from './RibbonControlDefinitionProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { lazyGetAttachmentSuggestionsMenuItems } from 'owa-attachment-suggestions';
import { getIsViewModeSelected } from 'owa-command-ribbon-store';
import isFeatureEnabled from 'owa-feature-flags/lib/utils/isFeatureEnabled';
import { CommandingViewMode } from 'owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions';
import type { ComposeViewState } from 'owa-mail-compose-store';
import isCLPEnabled from 'owa-mail-protection/lib/utils/clp/isCLPEnabled';
import type { RibbonTabId, RibbonControlId } from 'owa-ribbon-ids';
import { MailRibbonTabId, MailRibbonControlId } from 'owa-ribbon-ids';
import { isConsumer } from 'owa-session-store';

/**
 * This function decides whether a control should show or not.
 * For any logic on show/hiding controls, please put them here.
 *
 * @tabId the current Tab that is calling this function
 * @controlId the current control being tested to see whether it should show
 * @props Optional parameter, used for certain buttons to test whether they should show.
 *        Note: Props is only undefined in Customizer cases. Certain buttons use this logic truth
 *        when determining whether they should show.
 */
export const shouldShowRibbonControl = computedFn(
    (
        tabId: RibbonTabId,
        controlId: RibbonControlId,
        props: RibbonControlDefinitionProps | undefined
    ): boolean => {
        const isToolbarMode = getIsViewModeSelected(CommandingViewMode.CommandBar);
        switch (tabId) {
            case MailRibbonTabId.Tab_Home:
                switch (controlId) {
                    case MailRibbonControlId.Move:
                    case MailRibbonControlId.Rules:
                        if (!props) {
                            // We're in Customizer. We don't have props, but will show these controls always.
                            return true;
                        }

                        return !!props.showMoveRules;

                    case MailRibbonControlId.ToggleLeftPane:
                        return !isFeatureEnabled('mon-tri-collapsibleFolderPane');

                    case MailRibbonControlId.AddressBook:
                        return isFeatureEnabled('mon-ribbon-postSdf');

                    case MailRibbonControlId.BrowseGroups:
                    case MailRibbonControlId.GetAddIns:
                        return !isConsumer();

                    case MailRibbonControlId.RibbonCustomizer:
                        return isFeatureEnabled('mon-ribbon-customization');

                    default:
                        return true;
                }

            case MailRibbonTabId.Tab_Message:
                switch (controlId) {
                    case MailRibbonControlId.AssignPolicy: // Fallthrough
                    case MailRibbonControlId.FlagUnflag: // Fallthrough
                    case MailRibbonControlId.ImmersiveReader:
                    case MailRibbonControlId.CheckNames:
                        return isFeatureEnabled('mon-ribbon-postSdf');

                    case MailRibbonControlId.ToggleLeftPane:
                        if (isToolbarMode) {
                            return (
                                !isFeatureEnabled('mon-tri-collapsibleFolderPane') &&
                                !props?.popoutComposeViewState
                            );
                        } else {
                            return !isFeatureEnabled('mon-tri-collapsibleFolderPane');
                        }

                    case MailRibbonControlId.AttachFile:
                        if (!props) {
                            // We're in Customizer. We don't have props, but will show AttachFile always.
                            return true;
                        }

                        if (props.attachmentPickers) {
                            const viewState: ComposeViewState | undefined =
                                props.popoutComposeViewState ?? getMainWindowComposeViewState();
                            const suggestionsGetter = lazyGetAttachmentSuggestionsMenuItems.tryImportForRender();
                            return viewState != undefined && suggestionsGetter != undefined;
                        }
                        return false;

                    case MailRibbonControlId.NewMessage:
                        if (isToolbarMode) {
                            return !props?.popoutComposeViewState;
                        }
                        return true;

                    case MailRibbonControlId.Sensitivity:
                        return isCLPEnabled();

                    case MailRibbonControlId.Encrypt:
                        return isCLPEnabled() && isEncryptionAvailable();

                    case MailRibbonControlId.Encrypt_SingleButton:
                        return !isCLPEnabled() && isEncryptionAvailable();

                    case MailRibbonControlId.RibbonCustomizer:
                        return (
                            isFeatureEnabled('mon-ribbon-customization') &&
                            !props?.popoutComposeViewState
                        );

                    default:
                        return true;
                }

            case MailRibbonTabId.Tab_Insert:
                switch (controlId) {
                    case MailRibbonControlId.AttachFile:
                        if (!props) {
                            // We're in Customizer. We don't have props, but will show AttachFile always.
                            return true;
                        }

                        if (props.attachmentPickers) {
                            const viewState: ComposeViewState | undefined =
                                props.popoutComposeViewState ?? getMainWindowComposeViewState();
                            const suggestionsGetter = lazyGetAttachmentSuggestionsMenuItems.tryImportForRender();
                            return viewState != undefined && suggestionsGetter != undefined;
                        }
                        return false;
                    default:
                        return true;
                }

            case MailRibbonTabId.Tab_Format:
                switch (controlId) {
                    default:
                        return true;
                }

            case MailRibbonTabId.Tab_View:
                switch (controlId) {
                    case MailRibbonControlId.RibbonCustomizer:
                        return isFeatureEnabled('mon-ribbon-customization');

                    default:
                        return true;
                }

            case MailRibbonTabId.Tab_Notes:
                switch (controlId) {
                    default:
                        return true;
                }

            case MailRibbonTabId.Tab_Table_Format:
                switch (controlId) {
                    default:
                        return true;
                }

            default:
                throw new Error('getShouldShowControlsRules.ts: Unknown tab id: ' + tabId);
        }
    }
);
